import { useState } from "react";
import { DataValuesTable } from "@/components/contracts/DataValuesTable";
import { FieldGrid } from "@/components/contracts/FieldGrid";
import { MovementsTable } from "@/components/contracts/MovementsTable";
import { Badge } from "@/components/ui/badge";
import type { Contract, ContractMovement } from "@/lib/coach-manager/types";

/**
 * Full read-only display of one `Contract`: a full-width top strip (the
 * Contract's own fields + its data rows), then a full-width Movements table
 * below — click a row to expand an accordion showing that movement's field
 * grid and its Vehicles/Pickups/Setdowns/etc. tabs together, inline. Only one
 * movement's accordion is open at a time. Purpose-built (not a generic tree
 * renderer) since the shape is fixed and fully known from
 * `src/lib/coach-manager/types.ts`. No action buttons — display only.
 */
export function ContractDetail({ contract }: { contract: Contract }) {
  const movements = contract.ContractMovements ?? [];
  const [expandedMovementId, setExpandedMovementId] = useState<number | null>(
    movements[0]?.ContractMovementID ?? null,
  );

  const handleToggle = (movement: ContractMovement) => {
    setExpandedMovementId((current) =>
      current === movement.ContractMovementID ? null : movement.ContractMovementID,
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-[19px] font-bold">Contract {contract.ContractID}</h3>
          <Badge variant={contract.Cancelled ? "destructive" : "success"}>
            {contract.Cancelled ? "Cancelled" : "Active"}
          </Badge>
        </div>
        <FieldGrid
          fields={[
            { label: "Client ID", value: contract.ClientID },
            { label: "Booking Type", value: contract.BookingType },
            { label: "Company ID", value: contract.CompanyID },
            { label: "Base Serial No", value: contract.BaseSerialNo },
            { label: "Operator ID", value: contract.OperatorID },
            { label: "Date Created", value: contract.DateCreated },
          ]}
        />
        {contract.ContractData && contract.ContractData.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-display text-sm font-semibold">
              Contract Data ({contract.ContractData.length})
            </h4>
            <DataValuesTable rows={contract.ContractData} />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="font-display text-sm font-semibold">Movements ({movements.length})</h4>
        <p className="text-xs text-muted-foreground">
          Select a movement to view its detail, vehicles, pickups and setdowns.
        </p>
        <MovementsTable
          movements={movements}
          expandedMovementId={expandedMovementId}
          onToggle={handleToggle}
        />
      </div>
    </div>
  );
}
